import { DOCUMENT } from '@angular/common';
import {
  type AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { Currency } from '@principle-theorem/accounting';
import { ContentContainerComponent } from '@principle-theorem/ng-principle-shared';
import {
  ElementTargetDirective,
  NgCommonModule,
} from '@principle-theorem/ng-shared';
import {
  DEPOSIT_SYSTEM_TEMPLATES,
  DepositSystemTemplate,
  SYSTEM_TEMPLATES_IDS,
} from '@principle-theorem/principle-core';
import {
  CancellationFeeAttribution,
  DEPOSIT_BOOKING_CONTEXT_LABELS,
  DEPOSIT_BOOKING_CONTEXTS,
  DEPOSIT_CAPTURE_STRATEGY_TEXT,
  DepositCaptureStrategy,
  DepositForfeiturePolicy,
  type IDepositOptionText,
  IDepositSettings,
  type IDepositTreatmentOverride,
  ISchedulingEventReason,
  type ITreatmentConfiguration,
  type ITreatmentTemplate,
  SystemTemplate,
} from '@principle-theorem/principle-core/interfaces';
import {
  type DocumentReference,
  type WithRef,
} from '@principle-theorem/shared';
import { BehaviorSubject, combineLatest, merge, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import {
  DEPOSIT_CANCELLATION_FEE_ATTRIBUTION_TEXT,
  DEPOSIT_FORFEITURE_POLICY_TEXT,
  DEPOSIT_TIMING_DEFAULT_HINT,
  DEPOSIT_TIMING_DEFAULT_QUESTION,
} from './deposit-configuration-form/deposit-vocabulary';
import { DepositSectionHeadingComponent } from './deposit-section-heading.component';
import {
  DepositForfeitureRuleFormGroup,
  DepositSettingsFormGroup,
} from './deposit-settings-form.formgroup';
import { DepositTemplateRowComponent } from './deposit-template-row.component';
import {
  DepositTreatmentConfigurationSettingsComponent,
  type IDepositTreatmentConfigurationState,
} from './deposit-treatment-configuration-settings.component';
import {
  DepositTreatmentTemplateRows,
  type IDepositTreatmentTemplateGroup,
  type IDepositTreatmentTemplateRow,
} from './deposit-treatment-template-rows';

type DepositCommunicationControlType = 'toggle' | 'always-sent' | 'automation';

interface IDepositCommunicationEntry {
  readonly displayName: string;
  readonly systemTemplate: SystemTemplate;
  readonly purpose: string;
  readonly controlType: DepositCommunicationControlType;
  readonly formControlName?:
    | 'forfeitureEmailEnabled'
    | 'refundConfirmationEmailEnabled';
  readonly accessibleName?: string;
}

interface IDepositForfeitureReasonRow {
  readonly reason: WithRef<ISchedulingEventReason>;
  readonly rule?: DepositForfeitureRuleFormGroup;
}

@Component({
  selector: 'pr-deposit-settings-form',
  templateUrl: './deposit-settings-form.component.html',
  styleUrl: './deposit-settings-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgCommonModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatIconModule,
    RouterModule,
    ElementTargetDirective,
    ContentContainerComponent,
    DepositTreatmentConfigurationSettingsComponent,
    DepositTemplateRowComponent,
    DepositSectionHeadingComponent,
  ],
})
export class DepositSettingsFormComponent
  implements OnDestroy, AfterViewChecked
{
  private _document = inject(DOCUMENT);
  private _route = inject(ActivatedRoute);
  private _pendingFragment?: string;
  private _onDestroy$ = new Subject<void>();
  private _reasons: WithRef<ISchedulingEventReason>[] = [];
  private _settings?: IDepositSettings;
  private _templates: WithRef<ITreatmentTemplate>[] = [];
  private _templateOverridesDirty$ = new BehaviorSubject(false);
  private _treatmentConfigurationState$ =
    new BehaviorSubject<IDepositTreatmentConfigurationState>({
      dirty: false,
      valid: true,
    });
  bookingContexts = DEPOSIT_BOOKING_CONTEXTS;
  bookingContextLabels = DEPOSIT_BOOKING_CONTEXT_LABELS;
  readonly depositTimingDefaultQuestion = DEPOSIT_TIMING_DEFAULT_QUESTION;
  readonly depositTimingDefaultHint = DEPOSIT_TIMING_DEFAULT_HINT;
  readonly captureStrategyText = DEPOSIT_CAPTURE_STRATEGY_TEXT;
  readonly forfeiturePolicyText: Record<
    DepositForfeiturePolicy,
    IDepositOptionText
  > = {
    [DepositForfeiturePolicy.Auto]: DEPOSIT_FORFEITURE_POLICY_TEXT.auto,
    [DepositForfeiturePolicy.Manual]: DEPOSIT_FORFEITURE_POLICY_TEXT.manual,
  };
  readonly captureStrategyOptions: {
    value: DepositCaptureStrategy;
    text: IDepositOptionText;
  }[] = [
    DepositCaptureStrategy.ChargeOnBookingRequest,
    DepositCaptureStrategy.CaptureOnBookingRequestApproval,
    DepositCaptureStrategy.CaptureOnCancellationOrReschedule,
  ].map((value) => ({ value, text: DEPOSIT_CAPTURE_STRATEGY_TEXT[value] }));
  readonly forfeiturePolicyOptions: {
    value: DepositForfeiturePolicy;
    text: IDepositOptionText;
  }[] = [
    {
      value: DepositForfeiturePolicy.Auto,
      text: DEPOSIT_FORFEITURE_POLICY_TEXT.auto,
    },
    {
      value: DepositForfeiturePolicy.Manual,
      text: DEPOSIT_FORFEITURE_POLICY_TEXT.manual,
    },
  ];
  readonly cancellationFeeAttributionText =
    DEPOSIT_CANCELLATION_FEE_ATTRIBUTION_TEXT;
  readonly cancellationFeeAttributionOptions: {
    value: CancellationFeeAttribution;
    text: IDepositOptionText;
  }[] = [
    CancellationFeeAttribution.Practice,
    CancellationFeeAttribution.Practitioner,
  ].map((value) => ({
    value,
    text: DEPOSIT_CANCELLATION_FEE_ATTRIBUTION_TEXT[value],
  }));
  readonly depositCommunicationRecord: Record<
    DepositSystemTemplate,
    IDepositCommunicationEntry
  > = {
    [SystemTemplate.DepositRequestSMS]: {
      displayName: 'Deposit Request SMS',
      systemTemplate: SystemTemplate.DepositRequestSMS,
      purpose:
        'Sent when staff use the SMS action to request a deposit from the booking workflow. Carries the payment link.',
      controlType: 'always-sent',
    },
    [SystemTemplate.DepositRequestEmail]: {
      displayName: 'Deposit Request Email',
      systemTemplate: SystemTemplate.DepositRequestEmail,
      purpose:
        'Sent when staff use the email action to request a deposit from the booking workflow. Carries the payment link.',
      controlType: 'always-sent',
    },
    [SystemTemplate.DepositConfirmationEmail]: {
      displayName: 'Deposit Confirmation Email',
      systemTemplate: SystemTemplate.DepositConfirmationEmail,
      purpose:
        'Sent to the patient once their deposit payment clears, confirming the amount received. Covers both staff-requested deposits and online bookings. Pre-authorised deposits are confirmed when the payment is captured.',
      controlType: 'always-sent',
    },
    [SystemTemplate.DepositReminderEmail]: {
      displayName: 'Deposit Reminder Email',
      systemTemplate: SystemTemplate.DepositReminderEmail,
      purpose:
        'Sent to the patient before their appointment if their deposit is still unpaid.',
      controlType: 'automation',
    },
    [SystemTemplate.DepositForfeitNoticeEmail]: {
      displayName: 'Deposit Forfeit Notice Email',
      systemTemplate: SystemTemplate.DepositForfeitNoticeEmail,
      purpose:
        'Sent to the patient when staff retain their deposit after a cancellation or reschedule.',
      controlType: 'toggle',
      formControlName: 'forfeitureEmailEnabled',
      accessibleName: 'Send forfeit notification email',
    },
    [SystemTemplate.DepositRefundConfirmationEmail]: {
      displayName: 'Deposit Refund Confirmation Email',
      systemTemplate: SystemTemplate.DepositRefundConfirmationEmail,
      purpose: 'Sent to the patient when a deposit refund is processed.',
      controlType: 'toggle',
      formControlName: 'refundConfirmationEmailEnabled',
      accessibleName: 'Send refund confirmation email',
    },
  };

  readonly depositCommunicationEntries: IDepositCommunicationEntry[] =
    DEPOSIT_SYSTEM_TEMPLATES.map(
      (template) => this.depositCommunicationRecord[template]
    );
  form = new DepositSettingsFormGroup();
  reasonRows: IDepositForfeitureReasonRow[] = [];
  publicTreatmentRows: IDepositTreatmentTemplateRow[] = [];
  privateTreatmentRows: IDepositTreatmentTemplateRow[] = [];
  treatmentTemplateGroups: IDepositTreatmentTemplateGroup[] = [];
  treatmentConfigurationDeposits: IDepositSettings['treatmentConfigurationDeposits'] =
    {};
  canSave$ = combineLatest([
    merge(this.form.statusChanges, this.form.valueChanges).pipe(
      startWith(undefined)
    ),
    this._templateOverridesDirty$,
    this._treatmentConfigurationState$,
  ]).pipe(
    map(
      ([, templateOverridesDirty, treatmentConfigurationState]) =>
        this.form.valid &&
        this._templateRowsValid() &&
        treatmentConfigurationState.valid &&
        (this.form.dirty ||
          templateOverridesDirty ||
          treatmentConfigurationState.dirty)
    )
  );

  @ViewChild(DepositTreatmentConfigurationSettingsComponent)
  treatmentConfigurationSettings?: DepositTreatmentConfigurationSettingsComponent;

  @Input() brandUid?: string;
  @Input() currency: Currency = Currency.AUD;
  @Input() treatmentConfigurations: WithRef<ITreatmentConfiguration>[] = [];
  @Input() hasReminderAutomation = false;
  @Input() busy = false;

  @Output() readonly createReminderAutomation = new EventEmitter<void>();

  @Input()
  set reasons(reasons: WithRef<ISchedulingEventReason>[]) {
    this._reasons = reasons;
    this._updateReasonRows();
  }

  @Input()
  set treatmentTemplates(templates: WithRef<ITreatmentTemplate>[]) {
    this._templates = templates;
    this._updateTreatmentTemplateRows();
  }

  @Input()
  set value(value: IDepositSettings | undefined) {
    this._settings = value;
    this.treatmentConfigurationDeposits =
      value?.treatmentConfigurationDeposits ?? {};
    if (value) {
      this.form.patchSettings(value);
      this._updateReasonRows();
      this._updateSubControlState(!!value.enabled);
      this.form.updateCancellationPolicyUrlValidators();
    }
    this._updateTreatmentTemplateRows();
  }

  constructor() {
    this._updateSubControlState(!!this.form.controls.enabled.value);
    this.form.updateCancellationPolicyUrlValidators();

    this.form.controls.enabled.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((enabled) => {
        this._updateSubControlState(!!enabled);
      });

    this.form.controls.forfeitureEmailEnabled.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(() => this.form.updateCancellationPolicyUrlValidators());

    this._route.fragment
      .pipe(takeUntilDestroyed())
      .subscribe((fragment) => (this._pendingFragment = fragment ?? undefined));
  }

  ngAfterViewChecked(): void {
    this._scrollToPendingFragment();
  }

  get valid(): boolean {
    return (
      this.form.valid &&
      this._templateRowsValid() &&
      (this.treatmentConfigurationSettings?.valid ?? true)
    );
  }

  get hasPatientDepositAmount(): boolean {
    return Number.isFinite(
      this.form.controls.patientDepositAmountDollars.value
    );
  }

  clearPatientDepositAmount(): void {
    const control = this.form.controls.patientDepositAmountDollars;
    control.setValue(undefined);
    control.markAsDirty();
  }

  treatmentConfigurationStateChanged(
    state: IDepositTreatmentConfigurationState
  ): void {
    this._treatmentConfigurationState$.next(state);
  }

  forfeitureReasonSelectionChanged(
    reasonRef: DocumentReference<ISchedulingEventReason>,
    selected: boolean
  ): void {
    if (selected) {
      this.form.selectForfeitureReason(reasonRef);
    } else {
      this.form.deselectForfeitureReason(reasonRef);
    }
    this._updateReasonRows();
  }

  getRawValue(): IDepositSettings {
    const treatmentTemplateOverrides = [
      ...this.publicTreatmentRows,
      ...this.privateTreatmentRows,
    ].reduce<Record<string, IDepositTreatmentOverride>>(
      (overrides, row) =>
        row.form.dirty
          ? { ...overrides, [row.template.ref.id]: row.form.toOverride() }
          : overrides,
      this._settings?.treatmentTemplateOverrides ?? {}
    );

    return {
      ...this.form.toSettings(),
      treatmentTemplateOverrides,
      treatmentConfigurationDeposits:
        this.treatmentConfigurationSettings?.getRawValue() ??
        this._settings?.treatmentConfigurationDeposits ??
        {},
    };
  }

  treatmentTemplateName(templateUid: string): string | undefined {
    return [...this.publicTreatmentRows, ...this.privateTreatmentRows].find(
      (row) => row.template.ref.id === templateUid
    )?.template.name;
  }

  markAsPristine(): void {
    this.form.markAsPristine();
    for (const row of [
      ...this.publicTreatmentRows,
      ...this.privateTreatmentRows,
    ]) {
      row.form.markAsPristine();
    }
    this._templateOverridesDirty$.next(false);
    this.treatmentConfigurationSettings?.markAsPristine();
  }

  templateLink(
    systemTemplate: SystemTemplate,
    brandUid?: string
  ): string[] | undefined {
    if (!brandUid) {
      return;
    }
    return [
      '/settings',
      'brand',
      brandUid,
      'document-templates',
      SYSTEM_TEMPLATES_IDS[systemTemplate],
    ];
  }

  automationsLink(brandUid?: string): string[] | undefined {
    if (!brandUid) {
      return;
    }
    return ['/settings', 'brand', brandUid, 'automations'];
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  /**
   * The router's `anchorScrolling: 'enabled'` cannot reach these headings: it
   * drives the document scroller, and the settings shell scrolls an inner
   * container. `scrollIntoView` finds the nearest scrollable ancestor instead.
   *
   * Runs after every check rather than once on init, because the headings do
   * not exist until the brand's settings load and deposits are switched on.
   */
  private _scrollToPendingFragment(): void {
    if (!this._pendingFragment) {
      return;
    }
    const target = this._document.getElementById(this._pendingFragment);
    if (!target) {
      return;
    }
    this._pendingFragment = undefined;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private _updateSubControlState(enabled: boolean): void {
    const controls = [
      this.form.controls.appliesTo,
      this.form.controls.captureStrategy,
      this.form.controls.forfeitureRules,
      this.form.controls.forfeiturePolicy,
      this.form.controls.cancellationFeeAttribution,
      this.form.controls.forfeitureEmailEnabled,
      this.form.controls.refundConfirmationEmailEnabled,
      this.form.controls.autoApplyAppointmentAccountCredits,
      this.form.controls.patientDepositAmountDollars,
      this.form.controls.cancellationPolicyUrl,
    ];

    for (const control of controls) {
      if (enabled) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }
    }
    this.form.updateValueAndValidity();
  }

  private _updateReasonRows(): void {
    this.reasonRows = this._reasons.map((reason) => ({
      reason,
      rule: this.form.findForfeitureRule(reason.ref),
    }));
  }

  private _updateTreatmentTemplateRows(): void {
    if (this._templateOverridesDirty$.value) {
      return;
    }
    const rows = DepositTreatmentTemplateRows.create({
      templates: this._templates,
      settings: this._settings,
      markDirty: () => this._templateOverridesDirty$.next(true),
      onDestroy$: this._onDestroy$,
    });
    this.publicTreatmentRows = rows.publicRows;
    this.privateTreatmentRows = rows.privateRows;
    this.treatmentTemplateGroups = rows.groups;
    this._templateOverridesDirty$.next(false);
  }

  private _templateRowsValid(): boolean {
    return [...this.publicTreatmentRows, ...this.privateTreatmentRows].every(
      (row) => row.form.valid
    );
  }
}
